import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { FunctionalTrainerBuilder } from "../../functionalTrainerBuilder"
import type { FMContext } from "./fmParts"
import {
  createSideMeshes,
  createFrontBottomPulley,
  createResistanceByType,
  createBackBottomPulley,
  createTopRailPulley,
  createBottomRail,
  createHalfRackTopPulley,
  createTrolleyClip,
  createLatBarClip,
  createFootplate,
  createRearCrossmember,
} from "./fmParts"

// StandardFMBuilder — assembles FM-6 and FM-HR
export class StandardFMBuilder extends FunctionalTrainerBuilder {
  
  protected getModelPrefix(): string {
    return 'FM'
  }

  // MARK: Context Factory
  private createContext(materials: MaterialInfo): FMContext {
    return {
      config     : this.config,
      materials,
      height     : this.config.height,
      edition    : this.config.edition,
      isHalfRack : !this.config.sixPost,
    }
  }
  
  // MARK: Side Meshes
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] {
    return createSideMeshes(this.createContext(materials), left)
  }
  
  // MARK: Front Bottom Pulley
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    const context = this.createContext(materials)
    const trolleyClip = createTrolleyClip(left, this.config.trolleySelection)
    const carriage = this.buildSharedCarriage(trolleyClip, materials, left)
    
    return createFrontBottomPulley(context, left, trolleyClip, carriage)
  }

  // MARK: Resistance System
  protected addResistanceSystem(backUpright: PartDefinition, materials: MaterialInfo): void {
    const context = this.createContext(materials)
    const latBarClip = createLatBarClip(this.materialBuilder.createLatBarMaterial(), this.config.latBarSelection)
    
    const footplate = createFootplate(context)
    const resistance = createResistanceByType(context, latBarClip)
    const rearCrossmember = createRearCrossmember(context, footplate, resistance)

    backUpright.slots['crossmember-bottom'] = rearCrossmember
  }
  
  // MARK: Half Rack Attachment
  protected attachToHalfRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    left: boolean
  ): void {
    // Half rack has the pulleys in the same slot as the stack
    sideMeshes.push(frontBottomPulley)
    frontBottomPulley.offset = { y: 0, x: 0, z: -2 }
    
    const context = this.createContext(this.materialBuilder.createMainMaterial())
    const topPulley = createHalfRackTopPulley(context, left)
    
    topCrossmember.slots.side_2 = topPulley
    bottomCrossmember.slots.side_2 = sideMeshes
  }

  // MARK: Full Rack Attachment
  protected attachToFullRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void {
    const context = this.createContext(materials)
    const targetCrossmember = this.getBackBottomCrossmember(bottomCrossmember)
    
    // Add back bottom pulley to side meshes
    sideMeshes.push(createBackBottomPulley(context, left))
    
    // Attach rails system to front
    this.attachRailsSystem(context, frontBottomPulley, topCrossmember, left)
    
    // Attach side meshes to back crossmember
    targetCrossmember.slots[this.getSideSlotName()] = sideMeshes
    
    // Attach front bottom pulley to front crossmember
    bottomCrossmember.slots['4'] = [frontBottomPulley]
  }
  
  // MARK: Rails System Attachment
  private attachRailsSystem(
    context: FMContext,
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    left: boolean
  ): void {
    const depth = this.getDepthInInches()

    topCrossmember.slots.ftx = createTopRailPulley(context, depth, left)
    frontBottomPulley.slots.rail = createBottomRail(context, depth, left)
  }
}